<template>
  <div class="p-auth-layout" :class="{'p-auth-layout--screen-xl-only': isScreenXl}">
    <p-app-navigation></p-app-navigation>

    <v-main>
      <v-app-bar
        app
        color="defaultBgr"
        class="px-5 p-page-wrapper"
        flat
      >
        <v-toolbar-title class="p-header-1">
          {{ pageTitle }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <div class="p-app-bar-label">
          {{ $t('appBar.season') }}
        </div>
        <div class="p-app-bar mr-10">
          <v-select
            :items="seasons"
            :value="currentSeasonId"
            :loading="!seasonsLoaded"
            item-value="id"
            item-text="name"
            append-icon=""
            class="p-input-solo-filled font-weight-medium"
            solo
            dense
            @change="onCurrentSeasonChange"
          >
            <v-tooltip bottom slot="append-outer">
              <template v-slot:activator="{ on }">
                <router-link :to="{ name: 'settingsSeasons' }">
                  <v-icon v-on="on">mdi-cog-outline</v-icon>
                </router-link>
              </template>
              {{ $t('season.manageSeasons') }}
            </v-tooltip>
          </v-select>
        </div>
        <template v-if="projectsBySeason && projectsBySeason.length">
          <div class="p-app-bar-label">
            {{ $t('appBar.project') }}
          </div>
          <div class="p-app-bar">
            <v-select
              :items="projectsBySeason"
              :value="currentProjectId"
              :loading="!currentProjectId"
              item-value="id"
              item-text="title"
              class="font-weight-medium"
              outlined
              flat
              dense
              @change="_setCurrentProject"
            ></v-select>
          </div>
        </template>
        <router-link v-if="unreadEmails" :to="{ name: 'communicationEmails' }">
          <v-badge
            :value="unreadEmails"
            icon="mdi-email"
            overlap
            bordered
            offset-x="7"
            offset-y="7"
            class="ml-6 mr-6 mt-2"
          >
            <template v-slot:badge>
              {{ unreadEmails }}
            </template>
            <v-tooltip bottom v-if="unreadEmails">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="grey"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-email
                </v-icon>
              </template>
              <span>{{ $t('appBar.unreadEmailsInThisProject') }}</span>
            </v-tooltip>
          </v-badge>
        </router-link>
        <router-link v-if="unreadSms" :to="{ name: 'communicationSms' }">
          <v-badge
            :value="unreadSms"
            icon="mdi-bell"
            overlap
            bordered
            offset-x="7"
            offset-y="7"
            class="ml-6 mr-6 mt-2"
          >
            <template v-slot:badge>
              {{ unreadSms }}
            </template>
            <v-tooltip bottom v-if="unreadSms">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="grey"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-bell
                </v-icon>
              </template>
              <span>{{ $t('appBar.unreadSmsInThisProject') }}</span>
            </v-tooltip>
          </v-badge>
        </router-link>
        <span><websocket-component /></span>
        <p-user-avatar-menu v-if="currentUser"></p-user-avatar-menu>
      </v-app-bar>
      <v-progress-linear
        :active="pageLoading"
        :indeterminate="true"
        height="2"
        class="p-progress-linear"
      ></v-progress-linear>
      <slot v-if="loaded" />
    </v-main>
  </div>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import accessHelper from '@/helpers/access.helper'

import PAppNavigation from '@/components/PAppNavigation'
import PUserAvatarMenu from '@/components/PUserAvatarMenu'
import WebsocketComponent from '@/components/WebsocketComponent'
import snackbarMixin from '@/mixins/snackbar.mixin'
import windowSizeMixin from '@/mixins/windowSize.mixin'

import eventBus from '@/eventBus'

export default {
  components: {
    PAppNavigation,
    PUserAvatarMenu,
    WebsocketComponent
  },


  mixins: [
    snackbarMixin,
    windowSizeMixin
  ],


  data () {
    return {
      loaded: false,
      unreadEmails: null,
      unreadSms: null
    }
  },


  watch: {
    async currentSeasonId () {
      this.setCurrentProject()
      await this.loadCurrentUserAndProjectClaims(this.currentProjectId)
    },

    async currentProjectId () {
      await this.getNotification()
    }
  },


  computed: {
    ...mapState('pageSettings', ['pageTitle', 'pageLoading']),
    ...mapState('project', ['allProjects']),
    ...mapState('season', ['seasonsLoaded', 'seasons']),
    ...mapGetters('project', ['projectsBySeason', 'currentProjectId']),
    ...mapGetters('season', ['seasons', 'currentSeasonId']),
    ...mapGetters('user', ['currentUserId', 'currentUser', 'currentUserAndProjectClaims']),
    ...mapGetters('userState', ['userState']),


    env () {
      return process.env.NODE_ENV
    }
  },


  methods: {
    ...mapActions('project', ['loadAllProjects', 'setCurrentProject']),
    ...mapActions('season', ['loadSeasons', 'setCurrentSeason']),
    ...mapActions('user', ['loadCurrentUserAndProjectClaims']),
    ...mapActions('userState', ['updateUserState']),
    ...mapActions('notification', ['loadNotificationByProject']),

    initEventBus () {
      eventBus.$on('changeTenantSeasonProject', async (entity) => {
        this.onCurrentSeasonChange(entity.seasonId)
        console.info('entity === ', entity)
        if (entity.projectId) {
          await this._setCurrentProject(entity.projectId)
        }
        if (this.$router.currentRoute.name === entity.routeName) {
          eventBus.$emit('goToEntity', entity.text)
        } else {
          this.$router.push({
            name: entity.routeName,
            params: { goto: entity.text }
          })
        }
      })
    },

    async getNotification () {
      const params = {
        projectId: this.currentProjectId,
        onlyThisProject: true
      }
      const response = await this.loadNotificationByProject(params)
      this.unreadEmails = response.data.unreadEmails
      this.unreadSms = response.data.unreadSms
    },

    onCurrentSeasonChange (id) {
      this.setCurrentSeason(id)
      this.updateUserState({
        seasonId: id,
        projectId: this.currentProjectId
      })
    },

    async _setCurrentProject (projectId) {
      const currentUserAndProjectClaims = await this.loadCurrentUserAndProjectClaims(projectId)
      if (!accessHelper.isRouteAvailableForCurrentUserAndProject(this.$route.name, this.currentUser, currentUserAndProjectClaims)) {
        this.$router.push({ name: 'accessDenied' }).catch(() => {})
      }
      this.setCurrentProject(projectId)
      this.updateUserState({
        projectId
      })
    }
  },

  async created () {
    this.initEventBus()
    try {
      await this.loadSeasons()
      let seasonId = null
      if (this.userState?.seasonId && this.seasons.filter(season => season.id === this.userState.seasonId).length) {
        seasonId = this.userState.seasonId
      }
      this.setCurrentSeason(seasonId)
      this.updateUserState({
        seasonId
      })
      await this.loadAllProjects()

      const projectId = this.userState?.projectId || null
      this.setCurrentProject(projectId)
      await this.loadCurrentUserAndProjectClaims(this.currentProjectId)
      await this.getNotification()
      this.loaded = true
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    }
  }
}
</script>

<style>
  @import './../../node_modules/@syncfusion/ej2-base/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-buttons/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-inputs/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-popups/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-lists/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-navigations/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-splitbuttons/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-dropdowns/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-documenteditor/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-calendars/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-schedule/styles/material.css';
  @import './../../node_modules/@syncfusion/ej2-richtexteditor/styles/material.css';
</style>
